import React, { useState, useEffect, Suspense, lazy } from 'react'

//application specific
import './SmartAccess.css'
import Login from './common/login/Login'
import SetupMFA from './common/login/SetupMFA'
import SmartPearFooter from './common/components/SmartPearFooter'
import Gateway from './gateway/Gateway'
import NoClientAccess from './common/components/NoClientAccess'
import { fileDownload } from './common/fileDownload'
import Repository from './repository/Repository'

//aws cognito
import { Hub } from 'aws-amplify/utils'
import {
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  signOut
} from 'aws-amplify/auth'

// Bootstrap UI components
import Container from 'react-bootstrap/Container'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'

// Semantic UI components
import { Loader } from 'semantic-ui-react'

// the alert to show the user has been signed out
import SignedOut from './common/login/SignedOut'
import InactivityTimer from './common/components/InactivityTimer'
import {
  FORCE_SIGNED_OUT_COMMS_ERROR,
  FORCE_SIGNED_OUT_INACTIVE
} from './common/constants'
import SmartPearHeader from './common/components/SmartPearHeader'
import SmartPearBrandFooter from './common/components/SmartPearBrandFooter'
import { jsonFromStorage } from './common/fileDownload'
import FileNotFound from './gateway/FileNotFound'
import ForgottenPassword from './common/login/ForgottenPassword'
import Graph from './graph/graph.jsx'
import LoadingOverlay from './common/components/SmartLoadingOverlay'

// Lazy-load Guides so it doesn't block initial Gateway/Repository render
const Guides = lazy(() => import('./guides/Guides'))

//authentication statuses
const LOADING = 'nothing set yet'
const SIGN_IN = 'user needs to sign in'
const NO_CLIENT = "the URL doesn't identify a valid client"
const SIGNED_IN = 'The user is currently authenticated'
const USER_SIGNED_OUT = 'Successfully signed out.'
const CHANGE_PASSWORD = 'Change password'

// the container handling:
// - user sign in / out etc
// - headers / footers and branding
// - tabs containing services the client has access to
export default function SmartAccess({
  clientName,
  altClientName,
  clientId,
  initialIdentifier
}) {
  const [userAuth, setUserAuth] = useState({
    status: LOADING,
    userMFA: undefined,
    authSession: undefined,
    userAttributes: undefined
  })
  // initial state is null to indicate never tried to find gatewy yet
  const [gatewayFound, setGatewayFound] = useState(null)
  const [gatewayJwt, setGatewayJwt] = useState(null)
  const [showFileNotFound, setShowFileNotFound] = useState(false)
  const [downloadAttempted, setDownloadAttempted] = useState(false)
  const [configFound, setConfigFound] = useState(false)
  const [config, setConfig] = useState({
    allowed_folders: [],
    client_name: '',
    gateway_url: '',
    gateway_access: null,
    repository_access: null
  })
  const [activeTab, setActiveTab] = useState(null)
  const [visitedTabs, setVisitedTabs] = useState({})

  // When a tab is selected, mark it as mounted and
  // set it active
  const handleTabSelect = (key) => {
    setVisitedTabs((prev) => ({
      ...prev,
      [key]: true
    }))
    setActiveTab(key)
  }

  // Parse initial identifier (Device GUID or MPxN) from dev prop or hosted query params
  const DEVICE_ID_REGEX = /^[0-9A-Fa-f]{2}(?:-[0-9A-Fa-f]{2}){7}$/
  const MPXN_REGEX = /^(\d{13}|\d{1,10})$/
  const isValidIdentifier = (id) =>
    !!id && (DEVICE_ID_REGEX.test(id) || MPXN_REGEX.test(id))

  const searchString =
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.search === 'string'
      ? window.location.search
      : ''
  const urlParams = new URLSearchParams(searchString)

  // Prefer explicit id query param for device ID / MPxN deep links
  const idParam = urlParams.get('id') || undefined
  const hostedIdentifier = isValidIdentifier(idParam) ? idParam : undefined

  // Preserve existing behaviour: allow dev-provided identifier to override, but fall back to hosted id
  const initialIdentifierResolved = isValidIdentifier(initialIdentifier)
    ? initialIdentifier
    : hostedIdentifier

  // Derive potential download key from either explicit `download` param or from the first query param name
  const downloadParam = urlParams.get('download') || ''
  let derivedDownloadKey = downloadParam
  if (!derivedDownloadKey && searchString.startsWith('?')) {
    const firstKey = Array.from(urlParams.keys())[0]
    // If the first key looks like an S3 path (contains '/') treat it as the download key
    if (firstKey && firstKey.includes('/')) {
      derivedDownloadKey = firstKey
    }
  }

  // for first render only, so [] as the inputs
  useEffect(() => {
    if (clientId) {
      // register the handleAuth function to intercept Auth events
      Hub.listen('auth', handleAuth)
      checkUserStatus()
    } else {
      setUserAuth({
        ...userAuth,
        status: NO_CLIENT
      })
    }
  }, [])

  // handle Auth events, so when the user signs in / out etc
  const handleAuth = (data) => {
    const { payload } = data
    switch (payload.event) {
      case 'signedIn':
        checkUserStatus()
        break
      default:
        break
    }
  }

  // check whether there is a signed in user or not
  const checkUserStatus = async () => {
    try {
      const authSession = await fetchAuthSession()
      const userAttributes = await fetchUserAttributes()
      const { enabled } = await fetchMFAPreference()
      if (authSession) {
        window.history.replaceState(null, null, ' ')
        setUserAuth({
          status: SIGNED_IN,
          userMFA: enabled,
          authSession,
          userAttributes
        })
        const accessToken = authSession.tokens.accessToken.toString()
        setGatewayJwt(accessToken)
      }
    } catch (error) {
      if (!window.location.href.includes('id_token')) requireSignIn()
    }
  }

  // set status to require sign in
  const requireSignIn = () => {
    setUserAuth({
      ...userAuth,
      status: SIGN_IN
    })
  }

  //if user is inactive due to timeout or token expiry
  const userSignOut = async (status) => {
    await signOut().catch((e) => console.log(e))
    setUserAuth({
      ...userAuth,
      status: status
    })
  }

  //if user wants to change password while signed in
  const changePassword = async () => {
    setUserAuth({
      ...userAuth,
      status: CHANGE_PASSWORD
    })
  }

  // whenever user or its status changes and on first load
  useEffect(() => {
    if (userAuth.status === SIGNED_IN) {
      getConfig()
    }
  }, [userAuth])

  // see if the gateway is there
  useEffect(() => {
    if (configFound) {
      if (config.gateway_access) {
        const gatewayAlive = async () => {
          const gatewayResponse = await fetch(config.gateway_url).catch(() =>
            setGatewayFound(false)
          )
          setGatewayFound(!!(gatewayResponse && gatewayResponse.ok))
        }
        gatewayAlive()
      } else {
        // config.gateway_access is not true
        setGatewayFound(false) // explicitly set to false (not null)
      }
    }
  }, [configFound])

  //get this client's config from S3. Note that all attribute names will be in snake case e.g. gateway_access
  const getConfig = async () => {
    const jsonConfig = await jsonFromStorage(
      `config/${clientId}/front_end_config.json`
    )
    const clientNameGood =
      jsonConfig.client_name.toLowerCase().split(' ').join('') === clientName
    if (clientNameGood) {
      if (altClientName && jsonConfig.gateway_url) {
        jsonConfig.gateway_url = jsonConfig.gateway_url
          .split(clientName)
          .join(altClientName)
      }
      setConfig(jsonConfig)
      setConfigFound(true)
    } else {
      console.log('Client name mismatch')
    }
  }

  const repositoryAccess = config && config.repository_access
  // file download is triggered only via explicit query param: ?download=<s3Key>
  // or via an ad-hoc URL where the s3Key is the first query param name, e.g. ?ad_hoc_reports%2Fsrv...csv=
  if (
    derivedDownloadKey !== '' &&
    /\.[A-Za-z0-9]+$/i.test(derivedDownloadKey.toLowerCase()) &&
    userAuth.status === SIGNED_IN &&
    downloadAttempted === false
  ) {
    fileDownload(derivedDownloadKey, undefined, () => setShowFileNotFound(true))
    setDownloadAttempted(true)
  }

  // Once config is loaded and gateway/repository availability are known, choose
  // the most appropriate initial tab. This runs whenever the access flags change
  // (e.g. after async checks) and keeps the active tab in sync.
  useEffect(() => {
    // Don't decide on an active tab until we have the valid state.
    // This avoids making a choice based only on initial null values
    // and getting the wrong answer
    if (gatewayFound == null || repositoryAccess == null || !configFound) {
      return
    }

    if (gatewayFound) {
      setActiveTab('gateway')
    } else if (repositoryAccess) {
      setActiveTab('repository')
    } else {
      setActiveTab('guides')
    }
  }, [configFound, gatewayFound, repositoryAccess])

  return (
    <div className="App">
      <InactivityTimer signOut={userSignOut} />
      {userAuth.status === NO_CLIENT && <p>There is nothing here</p>}
      {(userAuth.status === FORCE_SIGNED_OUT_INACTIVE ||
        userAuth.status === FORCE_SIGNED_OUT_COMMS_ERROR ||
        userAuth.status === USER_SIGNED_OUT) && (
        <SignedOut
          show={true}
          signOutMessage={userAuth.status}
          clearSignedOut={() => requireSignIn()}
        />
      )}
      {userAuth.status === LOADING && (
        <Container>
          <div className="border border-medium">
            <Loader active inline="centered" />
          </div>
        </Container>
      )}
      {userAuth.status === SIGN_IN && (
        <Container>
          <br />
          <div className="border border-medium justify-content-center">
            <Login />
            <SmartPearBrandFooter clientName={clientName} />
          </div>
        </Container>
      )}
      {userAuth.status === SIGNED_IN && !userAuth.userMFA && (
        <Container>
          <div className="border border-medium justify-content-center">
            <SetupMFA email={userAuth.userAttributes.email} />
            <SmartPearBrandFooter clientName={clientName} />
          </div>
        </Container>
      )}
      {userAuth.status === SIGNED_IN && userAuth.userMFA?.includes('TOTP') && (
        <Container fluid>
          <SmartPearHeader
            email={userAuth.userAttributes.email}
            signOut={() => userSignOut(USER_SIGNED_OUT)}
            changePassword={() => changePassword()}
            clientName={clientName}
          />
          {/*
            avoid rendering the Tabs until we know which tab should be active
            Otherwise, we may briefly show the wrong tab.
          */}
          {activeTab === null ? (
            <LoadingOverlay active={true}>
              <div style={{ minHeight: '4rem' }} />
            </LoadingOverlay>
          ) : (
            <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
              {gatewayFound && (
                <Tab eventKey="gateway" title="Gateway">
                  <Gateway
                    clientId={clientId}
                    clientName={clientName}
                    email={userAuth.userAttributes.email}
                    gatewayUrl={config.gateway_url}
                    gatewayJwt={gatewayJwt}
                    setGatewayJwt={setGatewayJwt}
                    initialIdentifier={initialIdentifierResolved}
                    signOut={userSignOut}
                    exposeDccArchive={(
                      userAuth.authSession?.tokens?.idToken?.payload?.[
                        'cognito:groups'
                      ] ?? []
                    ).some(
                      (group) =>
                        group === 'developers' ||
                        group === 'smart_meter_experts'
                    )}
                  />
                </Tab>
              )}
              {repositoryAccess && (
                <Tab eventKey="repository" title="Repository">
                  <Repository
                    config={config}
                    clientName={clientName}
                    showFileNotFound={showFileNotFound}
                    setShowFileNotFound={() => setShowFileNotFound(true)}
                  />
                </Tab>
              )}
              {!repositoryAccess && configFound && (
                <Tab eventKey="repository" title="Repository">
                  <NoClientAccess />
                </Tab>
              )}
              {gatewayFound &&
                config &&
                config.graphs_config &&
                config.graphs_config.length > 0 && (
                  <Tab eventKey="graphs" title={config.graphs_tab_name}>
                    <Graph
                      config={config}
                      clientName={clientName}
                      authSession={userAuth.authSession}
                    />
                  </Tab>
                )}
              <Tab
                eventKey="guides"
                title={
                  // TODO: remove TemporaryHighlightText after promo period ends and replace with `title="Guides"`
                  <TemporaryHighlightText
                    text="Guides"
                    badgeText="NEW CRQ DOCS"
                    promoEndDate={new Date(2026, 3, 21)} // Months are 0-based: 0 = January
                  />
                }
              >
                {
                  // Avoids loading Guides (and making back end API call)
                  // until the user has clicked to view (activeTab),
                  // and keep it mounted once it has been visited, to
                  // preserve data from the back end call.
                  (activeTab === 'guides' || visitedTabs['guides']) && (
                    <Suspense
                      fallback={
                        <LoadingOverlay active={true}>
                          <div style={{ minHeight: '4rem' }} />
                        </LoadingOverlay>
                      }
                    >
                      <Guides
                        clientId={clientId}
                        clientName={clientName}
                        email={userAuth.userAttributes.email}
                        gatewayUrl={config.gateway_url}
                        gatewayJwt={gatewayJwt}
                        setGatewayJwt={setGatewayJwt}
                        initialIdentifier={initialIdentifierResolved}
                        signOut={userSignOut}
                        shouldLoadCrqExtraInfo={Boolean(
                          gatewayFound && activeTab === 'guides'
                        )}
                        config={config}
                      />
                    </Suspense>
                  )
                }
              </Tab>
            </Tabs>
          )}
          <SmartPearFooter />
        </Container>
      )}
      {userAuth.status === CHANGE_PASSWORD && <ForgottenPassword />}
      <FileNotFound
        show={showFileNotFound}
        clearFileNotFound={() => setShowFileNotFound(false)}
      />
    </div>
  )
}
/**
 * Temporarily decorates a piece of UI text with a "New" badge + flash styling.
 *
 * Renders the decorated version until `promoEndDate` is reached; after that it
 * renders only the plain `text` so callers can leave this in place until the
 * promo is fully removed.
 *
 * @example
 * <TemporaryHighlightText
 *   text="Guides"
 *   // JS Date months are 0-based: 1 = February
 *   promoEndDate={new Date(2026, 1, 15)}
 * />
 *
 * Retire a promo by replacing usage with plain text: e.g. `title="Guides"`
 */
function TemporaryHighlightText({
  text,
  promoEndDate = null,
  badgeText = 'New'
}) {
  if (promoEndDate && new Date() > promoEndDate) {
    return text
  }

  return (
    <span className="highlighted-text-wrapper">
      <span className="highlighted-text-new">{badgeText}</span>
      <span className="highlighted-text-flash">{text}</span>
    </span>
  )
}
